import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, User, ChevronRight, Newspaper } from 'lucide-react';
import { ShareButtons } from '../common/ShareButtons';
import { urlFor } from '../../lib/sanity';
import type { Post } from '../../types/sanity';

interface BlogPostCardProps {
  post: Post;
  featured?: boolean;
}

export function BlogPostCard({ post, featured = false }: BlogPostCardProps) {
  const [isHovered, setIsHovered] = React.useState(false);
  const hasImage = Boolean(post.mainImage);

  // Format date in a more readable way
  const formattedDate = new Date(post.publishedAt).toLocaleDateString('pl-PL', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  const titleClass = hasImage
    ? `${featured ? 'heading-3' : 'heading-4'} mb-3 group-hover:text-accessible-red-600 transition-colors line-clamp-2`
    : `${featured ? 'heading-2' : 'heading-3'} mb-3 group-hover:text-accessible-red-600 transition-colors line-clamp-3`;

  const excerptClass = hasImage
    ? 'body-medium mb-4 line-clamp-3 flex-grow'
    : 'body-medium mb-4 line-clamp-5 flex-grow';

  return (
    <article
      className={`bg-white dark:bg-accessible-gray-800 high-contrast:bg-black rounded-lg shadow-lg overflow-hidden group hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 flex flex-col ${featured ? 'md:col-span-2' : ''}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {hasImage ? (
        <Link
          to={post.slug?.current ? `/aktualnosci/${post.slug.current}` : '#'}
          className="block relative overflow-hidden"
        >
          <div className={`${featured ? 'aspect-[16/9]' : 'aspect-video'} relative overflow-hidden`}>
            <img
              src={urlFor(post.mainImage ?? null)
                .width(featured ? 1200 : 800)
                .height(featured ? 675 : 450)
                .quality(85)
                .url()}
              alt={post.title}
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              loading="lazy"
              decoding="async"
              onError={(e) => {
                console.error('Error loading image:', e);
                e.currentTarget.src = 'https://placehold.co/800x450/jpeg';
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

            {/* Categories overlay */}
            <div className="absolute top-4 left-4 flex flex-wrap gap-2">
              {post.categories?.map((category) => (
                <span
                  key={category.title}
                  className="px-3 py-1 bg-accessible-red-600 !text-white text-xs font-semibold leading-none rounded-full shadow-md"
                >
                  {category.title}
                </span>
              ))}
            </div>
          </div>
        </Link>
      ) : (
        // Image-less header: red gradient bar with categories and a small icon,
        // visually distinct from the placeholder grey box used previously.
        <Link
          to={post.slug?.current ? `/aktualnosci/${post.slug.current}` : '#'}
          className="block bg-gradient-to-br from-accessible-red-600 to-accessible-red-700 dark:from-accessible-red-700 dark:to-accessible-red-800 px-6 py-5 border-b-4 border-accessible-red-800 dark:border-accessible-red-900 high-contrast:bg-accessible-red-700"
          aria-label={post.title}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex flex-wrap gap-2">
              {post.categories?.map((category) => (
                <span
                  key={category.title}
                  className="px-3 py-1 bg-white/20 backdrop-blur-sm !text-white text-xs font-semibold leading-none rounded-full shadow-md"
                >
                  {category.title}
                </span>
              ))}
            </div>
            <Newspaper className="w-6 h-6 text-white/80 flex-shrink-0" aria-hidden="true" />
          </div>
        </Link>
      )}

      <div className="p-6 flex-1 flex flex-col">
        <Link to={post.slug?.current ? `/aktualnosci/${post.slug.current}` : '#'}>
          <h2 className={titleClass}>
            {post.title}
          </h2>
        </Link>

        <p className={excerptClass}>
          {post.excerpt}
        </p>
        
        <div className="mt-auto">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              {post.author && (
                <div className="flex items-center gap-2">
                  {post.author.image ? (
                    <img 
                      src={urlFor(post.author.image).width(40).height(40).url()}
                      alt={post.author.name}
                      className="w-8 h-8 rounded-full object-cover border-2 border-white dark:border-accessible-gray-700"
                      loading="lazy"
                      decoding="async"
                    />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-accessible-gray-200 dark:bg-accessible-gray-700 flex items-center justify-center">
                      <User className="w-4 h-4 text-accessible-gray-600 dark:text-white/90 high-contrast:text-white" />
                    </div>
                  )}
                  <span className="text-sm font-medium text-accessible-gray-700 dark:text-white high-contrast:text-white">
                    {post.author.name}
                  </span>
                </div>
              )}
            </div>
            
            <div className="flex items-center gap-2 text-accessible-gray-600 dark:text-white/90 high-contrast:text-white/70">
              <div className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                <time dateTime={post.publishedAt} className="text-xs text-accessible-gray-600 dark:text-white/90 high-contrast:text-white/70">
                  {formattedDate}
                </time>
              </div>
            </div>
          </div>
          
          <div className="flex items-center justify-end gap-4 pt-4 border-t border-accessible-gray-200 dark:border-accessible-gray-700 high-contrast:border-white/20">
            <ShareButtons
              url={`${window.location.origin}${post.slug?.current ? `/aktualnosci/${post.slug.current}` : ''}`}
              title={post.title}
              description={post.excerpt}
            />
            <Link
              to={post.slug?.current ? `/aktualnosci/${post.slug.current}` : '#'}
              className="inline-flex items-center text-accessible-red-600 hover:text-accessible-red-700 text-sm font-medium group"
            >
              Czytaj więcej
              <ChevronRight className={`w-4 h-4 ml-1 transition-transform duration-300 ${isHovered ? 'translate-x-1' : ''}`} />
            </Link>
          </div>
        </div>
      </div>
    </article>
  );
}